/**
 * The wire, as types.
 *
 * Every shape here is copied from `docs/api.md` § 1, which is the contract both lanes implement
 * against. The keys are short because the server sends them tens of thousands of times a second;
 * do not "tidy" them here, because the only thing that would achieve is a client that silently
 * reads `undefined` from every frame.
 *
 * Two properties of the real server matter more than the table in the doc, and both are load
 * bearing in the parser below:
 *
 *   - **A peaceful provider omits `z`, `c`, `bc`, `sid` and `startedAt`** rather than sending
 *     nulls, and `/api/state` also omits `lastEndedAt` when there has never been a siege. So every
 *     optional field is read as "absent or null", never as "present".
 *   - **`tick` carries only the providers currently under siege**, and every number in it is
 *     absolute. A dropped frame therefore costs nothing: the next one is still the whole truth.
 */

export type ProviderId = 'openai' | 'anthropic' | 'google'

/** Left to right, the order the squares are drawn in. */
export const PROVIDER_IDS: readonly ProviderId[] = ['openai', 'anthropic', 'google']

export function isProviderId(value: unknown): value is ProviderId {
  return value === 'openai' || value === 'anthropic' || value === 'google'
}

export type Impact = 'minor' | 'major' | 'critical'
export type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved'

export interface IncidentUpdate {
  status: IncidentStatus
  body: string
  createdAt: string
}

export interface Incident {
  id: string
  title: string
  impact: Impact
  status: IncidentStatus
  startedAt: string
  url?: string
  /**
   * The provider's own words about the outage.
   *
   * Never present on the socket — every frame there is capped at 256 bytes (`docs/api.md` § 1) and
   * an incident update is prose. It arrives on `/api/incidents` and `/api/sieges/{id}`, which is
   * why the drawer renders this section only when it is there rather than reserving space for it.
   */
  updates?: readonly IncidentUpdate[]
}

/**
 * The v2 game, as it rides on the wire (`docs/api.md` § 1.2, R-116).
 *
 * Additive to the v1 fields and present only in `siege`. `z` is the unbounded v2 count, `w` the
 * fallen walls (0–3), `hp` the *current* wall quantised to 0–1000 — a bar needs neither a float
 * nor a number. `fell` and `post` travel in `hello` and the `wall` event only; a `tick` carries the
 * three moving numbers. Until B24 is deployed the live socket omits the object entirely, and every
 * reader below treats "absent" exactly like a v1 client would: as nothing to draw.
 */
export interface WireWalls {
  z: number
  w: number
  hp: number
  fell?: string[]
  post?: (string | null)[]
}

export interface ProviderSnapshot {
  state: 'peaceful' | 'siege'
  down: boolean
  lastEndedAt?: string | null
  sid?: string | null
  startedAt?: string | null
  z?: number | null
  c?: number | null
  bc?: number | null
  breachUntil?: number | null
  /** The gate's health, 0–1000 (`docs/api.md` § 1.2). Absent from a server before the gate had health. */
  gh?: number | null
  walls?: WireWalls | null
  incidents: Incident[]
}

export interface ResetRecord {
  id: string
  kind: 'hard' | 'banked' | 'credits' | 'promo' | 'boost'
  at: string
  summary: string
  url?: string
  clicksTaken: number | null
}

export interface Hello {
  t: 'hello'
  v?: number
  now?: number
  tickHz?: 1 | 2
  ver?: number
  all: number
  reset: { c: number; last: ResetRecord | null }
  providers: Partial<Record<ProviderId, ProviderSnapshot>>
}

/** `GET /api/state`: the `hello` body minus `t` and `tickHz`. */
export type StateSnapshot = Omit<Hello, 't'>

export interface Tick {
  t: 'tick'
  all?: number
  r?: number
  p?: Partial<
    Record<ProviderId, { z: number; c: number; bc: number; gh?: number; walls?: WireWalls | null }>
  >
}

export interface Ack {
  t: 'ack'
  p: ProviderId | 'reset'
  n: number
  s?: number
  b?: 0 | 1
  z?: number
  r?: number
  /** The v2 count after the batch; absent when the square is not in siege (`docs/api.md` § 1.2). */
  zw?: number
}

export interface SiegeEvt {
  t: 'siege'
  p: ProviderId
  sid: string
  z: number
  startedAt: string
  incidents: Incident[]
}

export interface AllClearEvt {
  t: 'allclear'
  p: ProviderId
  sid: string
  endedAt: string
  clicks: number
  breaches: number
  durationS: number
  /** The siege's final v2 state (`docs/api.md` § 1.2); absent from a v1-only server. */
  walls?: { w: number; z: number }
}

/**
 * One wall down (`docs/api.md` § 1.2, R-114/R-116). Fires once per fallen wall; `w` is the new
 * fallen count, so `w: 3` is the overrun. `post` is the outbound post URL if one was made at the
 * moment of publishing, otherwise `null`.
 */
export interface WallEvt {
  t: 'wall'
  p: ProviderId
  sid: string
  w: number
  z: number
  at: string
  post: string | null
}

export interface IncidentsEvt {
  t: 'incidents'
  p: ProviderId
  down: boolean
  incidents: Incident[]
}

export interface ResetEvt {
  t: 'reset'
  id: string
  kind: ResetRecord['kind']
  summary: string
  url?: string
  clicksTaken: number
  at: string
}

export interface Hb {
  t: 'hb'
}

export interface Bye {
  t: 'bye'
  reason: string
}

export type ServerMsg =
  Hello | Tick | Ack | SiegeEvt | AllClearEvt | WallEvt | IncidentsEvt | ResetEvt | Hb | Bye

export type ClientMsg =
  { t: 'click'; p: ProviderId; n: number } | { t: 'rclick'; n: number } | { t: 'ping' }

/**
 * What a press can be aimed at: one provider's wall, or the Codex reset sign.
 *
 * `reset` is not a provider and never becomes one — it is a separate counter with its own frame
 * (`rclick`) and its own ack (`{"p":"reset"}`), which is why the union is written out here rather
 * than smuggled into `ProviderId`.
 */
export type ClickTarget = ProviderId | 'reset'

/** The `click`/`rclick` frame for a target, ready for `LiveConnection.send`. */
export function clickFrame(target: ClickTarget, n: number): ClientMsg {
  return target === 'reset' ? { t: 'rclick', n } : { t: 'click', p: target, n }
}

/** The same batch as a `POST /api/click` body (`docs/api.md` § 2). */
export function clickBody(target: ClickTarget, n: number): string {
  return JSON.stringify(target === 'reset' ? { r: true, n } : { p: target, n })
}

/** `POST /api/click` answers an `ack`; anything else is not one and must not be dispatched. */
export function parseAck(raw: unknown): Ack | null {
  if (typeof raw !== 'object' || raw === null) return null
  const ack = raw as Partial<Ack>
  if (ack.t !== 'ack') return null
  if (typeof ack.n !== 'number' || !Number.isFinite(ack.n)) return null
  if (ack.p !== 'reset' && !isProviderId(ack.p)) return null
  return ack as Ack
}

export type TransportStatus = 'connecting' | 'open' | 'closed' | 'fallback'

/**
 * What the page needs from a connection, and all it is allowed to know about one.
 *
 * Two things implement it and neither is a subclass of the other: `createLiveConnection` (a real
 * socket with a REST fallback under it) and `createDemoTransport` (a simulator in the tab). The
 * store, the click batcher and `main.tsx` are written against this and nothing else, which is what
 * makes `?demo=` worth having — the demo drives the production code path rather than a parallel
 * one that could quietly diverge from it.
 *
 * `send` returns whether the frame actually left. It never queues and never retries; that decision
 * belongs to the batcher, which is the only thing that knows what a click is worth.
 */
export interface Transport {
  connect(): void
  close(): void
  send(msg: ClientMsg): boolean
  readonly status: TransportStatus
}

const KNOWN_TYPES = new Set([
  'hello',
  'tick',
  'ack',
  'siege',
  'allclear',
  'wall',
  'incidents',
  'reset',
  'hb',
  'bye',
])

/**
 * Parse one text frame, tolerantly.
 *
 * Unknown `t` values return `null` instead of throwing, because forward compatibility is a
 * deployment property: a server rolled out ahead of the browser tab that is still open must be
 * able to add a frame type without every one of those tabs going blank. The same reasoning covers
 * malformed JSON, which is what a truncated frame or a proxy injecting an error page looks like.
 */
export function parseServerMsg(raw: string): ServerMsg | null {
  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return null
  const t = (json as { t?: unknown }).t
  if (typeof t !== 'string' || !KNOWN_TYPES.has(t)) return null
  return json as ServerMsg
}

/** `GET /api/state` answers a `hello` body without the envelope; put the envelope back on. */
export function helloFromState(state: StateSnapshot): Hello {
  return { ...state, t: 'hello' }
}
